/* eslint-disable max-lines -- this file is a flat list of zod schemas */
import { z } from "zod";

export const GetSummaryOpportunitiesDataResponseSchema = z.object({
  label: z.string(),
  value: z.number(),
  params: z.string(),
});

export const TotalCostsSchema = z.object({
  total: z.number(),
  compute: z.number(),
  storage: z.number(),
  serverless: z.number(),
  cloud_services: z.number(),
  ai_services: z.number(),
  other: z.number().optional(),
});

export const ComputeGraphResponseSchema = z.object({
  date: z.string(),
  compute: z.number().nullable(),
  compute_estimate: z.number().nullable(),
  percentage_compute: z.number(),
  is_estimate: z.boolean(),
});
export const AggregatedComputeGraphResponseSchema = z.object({
  graph: z.array(ComputeGraphResponseSchema),
  currency: z.string().optional(),
});

// Mirrors the backend /summary/v2/cost/other response row. The backend
// (`_aggregate_snowflake_other_data`) emits only these fixed fields:
//   - date         — the bucket date
//   - total        — sum across every usage_type on that date
//   - total_estimate / is_estimate — present only on the projection path
// Every other key is a dynamic per-usage_type number (snake-cased), with
// a matching `<key>_estimate` on the projection path. There is no fixed
// set of usage_type columns on the backend, so the frontend must not
// hard-code one either — `.catchall` accepts the dynamic numeric keys
// (nullable: the projection path emits null for categories with no
// prediction).
export const OtherGraphSchema = z
  .object({
    date: z.string(),
    is_estimate: z.boolean().nullish(),
    total: z.number().optional(),
    total_estimate: z.number().nullish(),
  })
  .catchall(z.number().nullable().optional());
export const OtherGraphResponseSchema = z.object({
  graph: z.array(OtherGraphSchema),
  currency: z.string().optional(),
});

export const StorageGraphResponseSchema = z.object({
  date: z.string(),
  storage: z.number().nullable(),
  storage_estimate: z.number().nullable(),
  stage: z.number().nullable(),
  stage_estimate: z.number().nullable(),
  failsafe: z.number().nullable(),
  failsafe_estimate: z.number().nullable(),
  hybrid_table_storage: z.number().nullable(),
  hybrid_table_storage_estimate: z.number().nullable(),
  total_storage: z.number().nullable(),
  total_storage_estimate: z.number().nullable(),
  percentage_storage: z.number(),
  percentage_stage: z.number(),
  percentage_failsafe: z.number(),
  percentage_hybrid_table_storage: z.number(),
  percentage_total_storage: z.number(),
  is_estimate: z.boolean(),
});
export const AggregatedStorageGraphResponseSchema = z.object({
  graph: z.array(StorageGraphResponseSchema),
  currency: z.string().optional(),
});

// The serverless cost endpoint returns 27 Snowflake billing categories
// plus a catch-all `other`. Newer categories are optional so responses
// from older backends still parse.
export const ServerlessGraphResponseSchema = z.object({
  date: z.string(),
  total_estimate: z.number().nullable(),
  total: z.number().nullable(),
  search_optimization: z.number().nullable(),
  automatic_clustering: z.number().nullable(),
  materialized_views: z.number().nullable(),
  snowpipe: z.number().nullable(),
  data_transfer: z.number().nullable(),
  query_acceleration: z.number().nullable(),
  percentage_total: z.number(),
  percentage_search_optimization: z.number(),
  percentage_automatic_clustering: z.number(),
  percentage_materialized_views: z.number(),
  percentage_snowpipe: z.number(),
  percentage_data_transfer: z.number(),
  percentage_query_acceleration: z.number(),
  is_estimate: z.boolean(),
  automatic_clustering_estimate: z.number().nullable(),
  data_transfer_estimate: z.number().nullable(),
  materialized_views_estimate: z.number().nullable(),
  query_acceleration_estimate: z.number().nullable(),
  search_optimization_estimate: z.number().nullable(),
  snowpipe_estimate: z.number().nullable(),
  snowpark_container_services: z.number().nullable().optional(),
  snowpark_container_services_estimate: z.number().nullable().optional(),
  trust_center: z.number().nullable().optional(),
  trust_center_estimate: z.number().nullable().optional(),
  replication: z.number().nullable().optional(),
  replication_estimate: z.number().nullable().optional(),
  sensitive_data_classification: z.number().nullable().optional(),
  sensitive_data_classification_estimate: z.number().nullable().optional(),
  data_quality_monitoring: z.number().nullable().optional(),
  data_quality_monitoring_estimate: z.number().nullable().optional(),
  reader_data_transfer: z.number().nullable().optional(),
  reader_data_transfer_estimate: z.number().nullable().optional(),
  serverless_tasks: z.number().nullable().optional(),
  serverless_tasks_estimate: z.number().nullable().optional(),
  snowpipe_streaming: z.number().nullable().optional(),
  snowpipe_streaming_estimate: z.number().nullable().optional(),
  serverless_alerts: z.number().nullable().optional(),
  serverless_alerts_estimate: z.number().nullable().optional(),
  table_optimization: z.number().nullable().optional(),
  table_optimization_estimate: z.number().nullable().optional(),
  outbound_privatelink_endpoint: z.number().nullable().optional(),
  outbound_privatelink_endpoint_estimate: z.number().nullable().optional(),
  hybrid_table_requests: z.number().nullable().optional(),
  hybrid_table_requests_estimate: z.number().nullable().optional(),
  telemetry_data_ingest: z.number().nullable().optional(),
  telemetry_data_ingest_estimate: z.number().nullable().optional(),
  copy_files: z.number().nullable().optional(),
  copy_files_estimate: z.number().nullable().optional(),
  reader_snowpark_container_services: z.number().nullable().optional(),
  reader_snowpark_container_services_estimate: z.number().nullable().optional(),
  outbound_privatelink_data_processed: z.number().nullable().optional(),
  outbound_privatelink_data_processed_estimate: z.number().nullable().optional(),
  automated_refresh_and_data_registration: z.number().nullable().optional(),
  automated_refresh_and_data_registration_estimate: z.number().nullable().optional(),
  internal_data_transfer: z.number().nullable().optional(),
  internal_data_transfer_estimate: z.number().nullable().optional(),
  adj_for_internal_data_transfer: z.number().nullable().optional(),
  adj_for_internal_data_transfer_estimate: z.number().nullable().optional(),
  logging: z.number().nullable().optional(),
  logging_estimate: z.number().nullable().optional(),
  snapshot: z.number().nullable().optional(),
  snapshot_estimate: z.number().nullable().optional(),
  other: z.number().nullable().optional(),
  other_estimate: z.number().nullable().optional(),
  percentage_snowpark_container_services: z.number().optional(),
  percentage_trust_center: z.number().optional(),
  percentage_replication: z.number().optional(),
  percentage_sensitive_data_classification: z.number().optional(),
  percentage_data_quality_monitoring: z.number().optional(),
  percentage_reader_data_transfer: z.number().optional(),
  percentage_serverless_tasks: z.number().optional(),
  percentage_snowpipe_streaming: z.number().optional(),
  percentage_other: z.number().optional(),
});

export const AggregatedServerlessGraphResponseSchema = z.object({
  graph: z.array(ServerlessGraphResponseSchema),
  currency: z.string().optional(),
});

export const CloudServicesGraphResponseSchema = z.object({
  date: z.string(),
  cloud_services: z.number().nullable(),
  cloud_services_estimate: z.number().nullable(),
  adj_for_incl_cloud_services: z.number().nullable(),
  adj_for_incl_cloud_services_estimate: z.number().nullable(),
  total: z.number().nullable(),
  total_estimate: z.number().nullable(),
  is_estimate: z.boolean(),
});

export const AggregatedCloudServicesGraphResponseSchema = z.object({
  graph: z.array(CloudServicesGraphResponseSchema),
  currency: z.string().optional(),
});

// One numeric column per AI-service breakdown bucket, each with a matching
// `<key>_estimate` on the projection path. The keys are declared explicitly
// (rather than a `.catchall`) because the typed graph fixtures are generated by
// a fishery factory, which needs statically-known keys — a catchall collapses
// the inferred type to a string-index signature the factory can't express.
// Every bucket MUST be listed: an undeclared numeric key is silently stripped
// by Zod, making `sum(components) < total` in the UI.
export const AiServicesGraphResponseSchema = z.object({
  date: z.string(),
  functions: z.number().nullable(),
  functions_estimate: z.number().nullable(),
  document_ai: z.number().nullable(),
  document_ai_estimate: z.number().nullable(),
  fine_tuning: z.number().nullable(),
  fine_tuning_estimate: z.number().nullable(),
  analyst: z.number().nullable(),
  analyst_estimate: z.number().nullable(),
  search_daily_aggregate: z.number().nullable(),
  search_daily_aggregate_estimate: z.number().nullable(),
  cortex_code_cli: z.number().nullable(),
  cortex_code_cli_estimate: z.number().nullable(),
  cortex_agent: z.number().nullable(),
  cortex_agent_estimate: z.number().nullable(),
  snowflake_intelligence: z.number().nullable(),
  snowflake_intelligence_estimate: z.number().nullable(),
  snowflake_cowork: z.number().nullable().optional(),
  snowflake_cowork_estimate: z.number().nullable().optional(),
  ai_inference: z.number().nullable(),
  ai_inference_estimate: z.number().nullable(),
  // Adjustments & Refunds bucket: frequently negative (refunds), so no
  // non-negative constraint — a `.nonnegative()` here would reject the wire
  // response and strip the whole row.
  ai_services_adjustments: z.number().nullable().optional(),
  ai_services_adjustments_estimate: z.number().nullable().optional(),
  ai_services_other: z.number().nullable().optional(),
  ai_services_other_estimate: z.number().nullable().optional(),
  cortex_code_snowsight: z.number().nullable().optional(),
  cortex_code_snowsight_estimate: z.number().nullable().optional(),
  cortex_code_desktop: z.number().nullable().optional(),
  cortex_code_desktop_estimate: z.number().nullable().optional(),
  reader_ai_services: z.number().nullable().optional(),
  reader_ai_services_estimate: z.number().nullable().optional(),
  reader_ai_inference: z.number().nullable().optional(),
  reader_ai_inference_estimate: z.number().nullable().optional(),
  reader_cortex_code_snowsight: z.number().nullable().optional(),
  reader_cortex_code_snowsight_estimate: z.number().nullable().optional(),
  total: z.number().nullable(),
  total_estimate: z.number().nullable(),
  is_estimate: z.boolean(),
});

export const AggregatedAiServicesGraphResponseSchema = z.object({
  graph: z.array(AiServicesGraphResponseSchema),
  currency: z.string().optional(),
});

// Future rows contain projected buckets but no actual usage. Keep this wire
// contract scoped to the estimate endpoint; actual rows still require all
// established buckets, so missing actual data remains a validation failure.
const AiServicesProjectedGraphResponseSchema = AiServicesGraphResponseSchema.partial({
  functions: true,
  document_ai: true,
  fine_tuning: true,
  analyst: true,
  search_daily_aggregate: true,
  cortex_code_cli: true,
  cortex_agent: true,
  snowflake_intelligence: true,
  ai_inference: true,
}).extend({ is_estimate: z.literal(true) });

export const AggregatedAiServicesEstimateGraphResponseSchema = z.object({
  graph: z.array(z.union([AiServicesGraphResponseSchema, AiServicesProjectedGraphResponseSchema])),
  currency: z.string().optional(),
});

export const CostPerAggregateSchema = z.object({
  date: z.string(),
  compute: z.number().nullable(),
  compute_estimate: z.number().nullable(),
  storage: z.number().nullable(),
  storage_estimate: z.number().nullable(),
  serverless: z.number().nullable(),
  serverless_estimate: z.number().nullable(),
  cloud_services: z.number().nullable(),
  cloud_services_estimate: z.number().nullable(),
  ai_services: z.number().nullable(),
  ai_services_estimate: z.number().nullable(),
  other: z.number().nullable().optional(),
  other_estimate: z.number().nullable().optional(),
  total: z.number().nullable(),
  total_estimate: z.number().nullable(),
  percentage_total: z.number(),
  percentage_compute: z.number(),
  percentage_storage: z.number(),
  percentage_serverless: z.number(),
  percentage_cloud_services: z.number(),
  percentage_ai_services: z.number(),
  percentage_other: z.number().optional(),
  is_estimate: z.boolean(),
  currency: z.string().optional(),
});

export const OverallCostSummaryResponseSchema = z.object({
  graph: z.array(CostPerAggregateSchema),
  currency: z.string().optional(),
});

export const AutoTuneSavingsByDateSchema = z.object({
  date: z.string(),
  value: z.number().nullable(),
  percentage_saved: z.number().nullable(),
  number_of_decisions: z.number().nullable(),
  min_value: z.number().nullable(),
  max_value: z.number().nullable(),
});

export const AutoTuneSavingsSummaryResponseSchema = z.object({
  graph: z.array(AutoTuneSavingsByDateSchema),
  currency: z.string().optional(),
});

export const AssistedSavingsResponseSchema = z.object({
  total_assisted_savings: z.number(),
  time_saved: z.string(),
  opportunities: z.number(),
});

export const OverallSavingsResponseSchema = z.object({
  total_assisted_savings: z.number(),
  time_saved: z.string(),
  total_autonomous_savings: z.number(),
  total_savings: z.number(),
  min_total_autonomous_savings: z.number(),
  max_total_autonomous_savings: z.number(),
  currency: z.string().optional(),
});

export const OverallSavingsEstimateResponseSchema = OverallSavingsResponseSchema.extend({
  currency: z.string(),
  total_assisted_savings_estimate: z.number(),
  total_autonomous_savings_estimate: z.number(),
  total_time_saved_estimate: z.string(),
  total_savings_estimate: z.number(),
});

export const scheduleConfigSchema = z.object({
  interval: z.enum(["daily", "weekly", "monthly"]),
  day_of_week: z.number().min(0).max(6).optional(),
  day_of_month: z.number().min(1).max(31).optional(),
  hour: z.number().min(0).max(23).optional(),
  minute: z.number().min(0).max(59).optional(),
  timezone: z.string().optional(),
});

export const costAnalysisSubscribeRequestSchema = z.object({
  emails: z.array(z.string()),
  instance_id: z.string().optional(),
  schedule: scheduleConfigSchema.optional(),
  enable_rca: z.boolean().optional().default(false),
  toggle_on: z.boolean().default(true),
});

export const costAnalysisSubscriptionResponseSchema = z.object({
  message: z.string(),
  subscription_active: z.boolean(),
  schedule: scheduleConfigSchema.optional(),
  emails: z.array(z.string()).optional(),
  enable_rca: z.boolean().optional(),
});

export const costAnalysisSubscriptionStatusResponseSchema = z.object({
  subscription_active: z.boolean(),
  schedule: scheduleConfigSchema.optional(),
  emails: z.array(z.string()).optional(),
  enable_rca: z.boolean().optional(),
  instance_id: z.number().optional(),
});

export const costAnalysisUnsubscribeRequestSchema = z.object({
  instance_id: z.string().nullable(),
});

export const costAnalysisUnsubscribeResponseSchema = z.object({
  message: z.string(),
  unsubscribed: z.boolean(),
});

export const subscriptionItemSchema = z.object({
  id: z.string(),
  emails: z.array(z.string()),
  schedule: scheduleConfigSchema,
  enable_rca: z.boolean().optional(),
  instance_id: z.string().optional(),
  created_at: z.string().optional(),
  next_run: z.string().optional(),
  status: z.enum(["active", "paused", "error"]).optional().default("active"),
});

export const costAnalysisSubscriptionsListResponseSchema = z.object({
  subscriptions: z.array(subscriptionItemSchema),
  total_count: z.number(),
});
